import { useEffect, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";

/** The search box, with what the register itself holds offered from the third letter on. */
export const SearchBox = ({ value, onChange, params = {}, testId = "project-search" }) => {
  const [typed, setTyped] = useState(value || "");
  const [hints, setHints] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  useEffect(() => setTyped(value || ""), [value]);

  useEffect(() => {
    const word = typed.trim();
    if (word.length < 2) {
      setHints([]);
      return undefined;
    }
    const t = setTimeout(() => {
      api
        .get("/projects/suggest", { params: { q: word, ...params } })
        .then((r) => setHints(r.data.suggestions || []))
        .catch(() => setHints([]));
    }, 220);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed]);

  useEffect(() => {
    const away = (e) => {
      if (box.current && !box.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, []);

  const take = (word) => {
    setTyped(word);
    onChange(word);
    setOpen(false);
  };

  return (
    <div className="relative min-w-[260px] flex-1" ref={box}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        data-testid={testId}
        placeholder="Search by name, code, agency, department or officer…"
        value={typed}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setTyped(e.target.value);
          setOpen(true);
          onChange(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") setOpen(false);
          if (e.key === "Escape") setOpen(false);
        }}
        className="h-11 rounded-none bg-card pl-9 pr-9"
      />
      {typed && (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={() => take("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors duration-200 hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {open && hints.length > 0 && (
        <div
          data-testid={`${testId}-suggestions`}
          className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto border border-border bg-popover shadow-lg"
        >
          {hints.map((h) => (
            <button
              key={`${h.kind}-${h.value}`}
              type="button"
              data-testid={`${testId}-suggestion-${h.value}`}
              onClick={() => take(h.value)}
              className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left text-sm transition-colors duration-200 hover:bg-muted"
            >
              <span className="truncate">{h.value}</span>
              <span className="shrink-0 font-display text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
                {h.kind}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
